import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AlertColor, AlertPropsColorOverrides } from "@mui/material";
import { OverridableStringUnion } from "@mui/types";
import { forgotPassword } from "./api/accountApi";
import { NotificationMessage } from "../shared/models/models";
import { ForgotPassword } from "./models/AccountModels";
import { SerializedError } from "../shared/models/errorHandlingModels";
import { serializeError, createErrorNotification } from "../shared/utils/errorUtils";

interface ForgotPasswordState {
  isLoading: boolean;
  isEmailSent: boolean;
  formNotificationMessage?: NotificationMessage;
  openSnackbar: boolean;
  alertMessage: string;
  severity: OverridableStringUnion<AlertColor, AlertPropsColorOverrides>;
  email: string | null;
}

const initialState: ForgotPasswordState = {
  isLoading: false,
  isEmailSent: false,
  formNotificationMessage: undefined,
  openSnackbar: false,
  alertMessage: "",
  severity: "error",
  email: null,
};

export const sendForgotPasswordEmail = createAsyncThunk(
  "forgotPassword/sendEmail",
  async (model: ForgotPassword, { rejectWithValue }) => {
    try {
      await forgotPassword(model);
      return { success: true, email: model.userName };
    } catch (error: any) {
      return rejectWithValue(serializeError(error));
    }
  }
);

const forgotPasswordSlice = createSlice({
  name: "forgotPassword",
  initialState,
  reducers: {
    resetState: (state) => {
      state.isLoading = false;
      state.isEmailSent = false;
      state.formNotificationMessage = undefined;
      state.openSnackbar = false;
      state.alertMessage = "";
      state.severity = "error";
      state.email = null;
    },
    showNotification: (state, action: PayloadAction<{
      alertMessage: string;
      severity: OverridableStringUnion<AlertColor, AlertPropsColorOverrides>;
    }>) => {
      state.openSnackbar = true;
      state.alertMessage = action.payload.alertMessage;
      state.severity = action.payload.severity;
    },
    hideNotification: (state) => {
      state.openSnackbar = false;
    },
    clearFormError: (state) => {
      state.formNotificationMessage = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendForgotPasswordEmail.pending, (state) => {
        state.isLoading = true;
        state.formNotificationMessage = undefined;
      })
      .addCase(sendForgotPasswordEmail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isEmailSent = true;
        state.email = action.payload.email;
      })
      .addCase(sendForgotPasswordEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.formNotificationMessage = createErrorNotification(action.payload as SerializedError);
      });
  },
});

export const {
  resetState,
  showNotification,
  hideNotification,
  clearFormError
} = forgotPasswordSlice.actions;

export default forgotPasswordSlice.reducer; 